import { useState } from 'react';
import {
  PLANS,
  PLAN_IDS,
  formatPrice,
  type BillingInfo,
  type EntitlementResponse,
  type PaidPlanId,
  type PlanId,
  type SubscriptionInfo,
} from '@repo/shared/plans';
import { formatDate } from '../labels.ts';
import { billingApi, type Entitlement } from '../lib/entitlement.ts';
import { Icon } from './Icon.tsx';
import { Modal } from './Modal.tsx';

interface PlansDialogProps {
  plan: PlanId;
  planExpiresAt: number | null;
  billing: BillingInfo | null;
  devPlans: boolean;
  entitlement: Entitlement | null;
  onEntitlement: (response: EntitlementResponse | null) => void;
  /** Nur in der Entwicklung gesetzt. */
  onActivateDev?: (plan: PlanId) => Promise<void>;
  onClose: () => void;
}

type Action = 'checkout' | 'switch' | 'cancel' | 'resume' | 'dev';
type Busy = { plan: PlanId; action: Action } | null;

const errorText = (cause: unknown) =>
  cause instanceof Error ? cause.message : 'Das hat nicht geklappt.';

export function PlansDialog({
  plan,
  planExpiresAt,
  billing,
  devPlans,
  entitlement,
  onEntitlement,
  onActivateDev,
  onClose,
}: PlansDialogProps) {
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const subscription = entitlement?.subscription ?? null;
  const token = entitlement?.token ?? null;
  // Dev-Tarife gibt es nur in der Entwicklung – im Live-Build fällt der Code ganz weg.
  const devMode = import.meta.env.DEV && devPlans && onActivateDev !== undefined;

  const run = async (next: NonNullable<Busy>, task: () => Promise<void>) => {
    setBusy(next);
    setError(null);
    try {
      await task();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(null);
    }
  };

  const startCheckout = async (id: PaidPlanId) => {
    setBusy({ plan: id, action: 'checkout' });
    setError(null);
    try {
      const { url } = await billingApi.checkout(id);
      window.location.assign(url); // weiter zur Stripe-Bezahlseite
    } catch (cause) {
      setError(errorText(cause));
      setBusy(null);
    }
  };

  const switchTo = (id: PaidPlanId) => {
    if (!token) return;
    void run({ plan: id, action: 'switch' }, async () => {
      onEntitlement(await billingApi.switchPlan(token, id));
    });
  };

  const setCancel = (cancel: boolean) => {
    if (!token || !subscription) return;
    void run({ plan: subscription.plan, action: cancel ? 'cancel' : 'resume' }, async () => {
      onEntitlement(await billingApi.setCancel(token, cancel));
      setConfirmCancel(false);
    });
  };

  if (confirmCancel && subscription) {
    const until =
      subscription.status === 'trialing' ? subscription.trialEnd : subscription.currentPeriodEnd;
    return (
      <Modal title="Vertrag kündigen" onClose={() => setConfirmCancel(false)}>
        <p className="dialog__text">
          Dein Abo <strong>{PLANS[subscription.plan].name}</strong> endet dann
          {until ? ` am ${formatDate(until)}` : ' zum Ende des laufenden Zeitraums'}. Bis dahin
          kannst du alles weiter nutzen, danach wird nichts mehr abgebucht.
        </p>
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="dialog__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setConfirmCancel(false)}
          >
            Zurück
          </button>
          <button
            type="button"
            className="button button--danger"
            disabled={busy !== null}
            onClick={() => setCancel(true)}
          >
            {busy?.action === 'cancel' ? 'Wird gekündigt …' : 'Jetzt kündigen'}
          </button>
        </div>
      </Modal>
    );
  }

  const devButton = (id: PlanId) => (
    <button
      type="button"
      className={`button${id === 'premium' ? ' button--primary' : ''}`}
      disabled={busy !== null}
      onClick={() =>
        void run({ plan: id, action: 'dev' }, async () => {
          await onActivateDev?.(id);
        })
      }
    >
      {busy?.plan === id ? 'Wird aktiviert …' : `Dev: ${PLANS[id].name} aktivieren`}
    </button>
  );

  const action = (id: PlanId) => {
    // Es läuft ein Stripe-Abo: verwalten oder wechseln
    if (subscription && token) {
      if (id === subscription.plan) {
        return (
          <SubscriptionStatus
            subscription={subscription}
            busy={busy}
            onCancel={() => setConfirmCancel(true)}
            onResume={() => setCancel(false)}
          />
        );
      }
      if (id === 'free') return null;
      return (
        <button
          type="button"
          className="button"
          disabled={busy !== null || subscription.cancelAtPeriodEnd}
          title={subscription.cancelAtPeriodEnd ? 'Erst die Kündigung zurücknehmen' : undefined}
          onClick={() => switchTo(id)}
        >
          {busy?.plan === id ? 'Wird gewechselt …' : `Zu ${PLANS[id].name} wechseln`}
        </button>
      );
    }

    if (id === plan) {
      return (
        <p className="plan__status">
          {planExpiresAt ? `Aktiv bis ${formatDate(planExpiresAt)}` : 'Dein Tarif'}
        </p>
      );
    }
    if (id === 'free') return devMode ? devButton(id) : null;
    if (billing) {
      const { trialDays, name } = PLANS[id];
      return (
        <button
          type="button"
          className={`button${id === 'premium' ? ' button--primary' : ''}`}
          disabled={busy !== null}
          onClick={() => void startCheckout(id)}
        >
          {busy?.plan === id
            ? 'Weiter zur Bezahlseite …'
            : trialDays > 0
              ? `${trialDays} Tage gratis testen`
              : `${name} abonnieren`}
        </button>
      );
    }
    if (devMode) return devButton(id);
    return (
      <button type="button" className="button" disabled>
        Bald verfügbar
      </button>
    );
  };

  return (
    <Modal title="Mehr Filter, bessere Treffer" onClose={onClose} wide>
      {billing?.mode === 'test' && (
        <p className="banner banner--info">
          Testmodus: Es fließt kein echtes Geld. Zum Ausprobieren mit der Karte 4242 4242 4242 4242,
          einem Datum in der Zukunft und beliebiger Prüfnummer bezahlen.
        </p>
      )}
      <div className="plans">
        {PLAN_IDS.map((id) => {
          const offer = PLANS[id];
          const showTrial = offer.trialDays > 0 && !subscription;
          return (
            <article key={id} className={`plan plan--${id}${id === plan ? ' plan--current' : ''}`}>
              {showTrial && <span className="plan__ribbon">{offer.trialDays} Tage gratis</span>}
              <h3>{offer.name}</h3>
              <p className="plan__price">
                {offer.priceCents === 0 ? (
                  'Kostenlos'
                ) : (
                  <>
                    {formatPrice(offer.priceCents)} <small>/ Monat</small>
                  </>
                )}
              </p>
              <ul>
                {offer.highlights.map((text) => (
                  <li key={text}>
                    <Icon name="check" size={16} /> {text}
                  </li>
                ))}
              </ul>
              {action(id)}
            </article>
          );
        })}
      </div>
      <p className="hint">{footerText(billing, devMode)}</p>
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

function SubscriptionStatus({
  subscription,
  busy,
  onCancel,
  onResume,
}: {
  subscription: SubscriptionInfo;
  busy: Busy;
  onCancel: () => void;
  onResume: () => void;
}) {
  const { status, trialEnd, currentPeriodEnd, cancelAtPeriodEnd } = subscription;
  const until = status === 'trialing' ? trialEnd : currentPeriodEnd;

  let text: string;
  if (cancelAtPeriodEnd)
    text = until ? `Gekündigt, läuft noch bis ${formatDate(until)}` : 'Gekündigt';
  else if (status === 'trialing')
    text = trialEnd ? `Gratis bis ${formatDate(trialEnd)}` : 'Gratis-Testphase';
  else if (status === 'past_due')
    text = 'Die Zahlung hat nicht geklappt. Stripe versucht es noch einmal.';
  else text = currentPeriodEnd ? `Verlängert sich am ${formatDate(currentPeriodEnd)}` : 'Aktiv';

  return (
    <div className="plan__manage">
      <p className={`plan__status${status === 'past_due' ? ' plan__status--warning' : ''}`}>
        {text}
      </p>
      {cancelAtPeriodEnd ? (
        <button type="button" className="button" disabled={busy !== null} onClick={onResume}>
          {busy?.action === 'resume' ? 'Wird fortgesetzt …' : 'Kündigung zurücknehmen'}
        </button>
      ) : (
        <button
          type="button"
          className="button button--ghost button--small"
          disabled={busy !== null}
          onClick={onCancel}
        >
          Vertrag hier kündigen
        </button>
      )}
    </div>
  );
}

function footerText(billing: BillingInfo | null, devPlans: boolean): string {
  if (billing?.trialRequiresCard) {
    return 'Für die Testphase hinterlegst du ein Zahlungsmittel. Abgebucht wird erst danach. Kündigst du vorher, zahlst du nichts. Monatlich kündbar.';
  }
  if (billing) {
    return 'Die Testphase geht ohne Zahlungsmittel. Weiter läuft das Abo nur, wenn du vor Ende eines hinterlegst. Monatlich kündbar.';
  }
  if (import.meta.env.DEV && devPlans) {
    return 'Entwicklungsmodus: Tarife werden ohne Zahlung freigeschaltet. Für echte Käufe STRIPE_SECRET_KEY setzen.';
  }
  return 'Die Bezahlung wird gerade eingerichtet.';
}
